import React, { useEffect, useState } from "react";
import Modal from "../Elements/Modal/Modal";
import MainButton from "../Elements/MainButton/MainButton";
import SecondButton from "../Elements/SecondButton/SecondButton";
import MainInput from "../Elements/MainInput/MainInput";
import ErrorMessage from "../Elements/ErrorMessage/ErrorMessage";
import SuccessMessage from "../Elements/SuccessMessage/SuccessMessage";
import { updateNotification } from "../../services/NotificationService";
import "./AdminModalForm.css";

// Admin modal for editing an existing notification
const AdminEditNotificationModal = ({ isOpen, onClose, notification, onUpdated }) => {
  // Form state for editable notification fields
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    type: "ok",
    seen: false,
  });

  // UI feedback and saving state
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Populate form when modal opens or notification changes
  useEffect(() => {
    if (isOpen && notification) {
      setFormData({
        name: notification.name || "",
        description: notification.description || "",
        type: notification.type || "ok",
        seen: Boolean(notification.seen),
      });
      setError("");
      setSuccess("");
    }
  }, [isOpen, notification]);

  // Do not render if closed or missing notification
  if (!isOpen || !notification) return null;

  // Handle text and checkbox changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setError("");
  };

  // Build payload for update API
  const buildPayload = () => ({
    name: formData.name.trim(),
    description: formData.description.trim(),
    type: formData.type,
    seen: formData.seen,
  });

  // Main submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      setSaving(true);

      const payload = buildPayload();

      await updateNotification({
        notification_id: notification.id,
        data: payload,
      });

      setSuccess("Notification updated successfully.");
      setTimeout(() => {
        onUpdated?.();
        onClose();
      }, 1000);
    } catch (err) {
      setError(err.message || "Failed to update notification");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={saving ? () => {} : onClose}
      title="Edit Notification"
      actions={
        <>
          <SecondButton onClick={onClose} disabled={saving}>
            Cancel
          </SecondButton>
          <MainButton onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </MainButton>
        </>
      }
    >
      {/* Notification edit form */}
      <form onSubmit={handleSubmit} className="admin-modal-form">
        {/* Name field */}
        <label>
          Name
          <MainInput
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            disabled={saving}
            placeholder="Notification title"
          />
        </label>

        {/* Description field */}
        <label>
          Description
          <MainInput
            type="textarea"
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            disabled={saving}
            rows="2"
            placeholder="Notification message"
          />
        </label>

        {/* Type selector */}
        <label>
          Type
          <div className="admin-modal-select-wrapper">
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              disabled={saving}
              className="admin-modal-select admin-modal-select-with-arrow"
            >
              <option value="ok">OK</option>
              <option value="warning">Warning</option>
              <option value="error">Error</option>
              <option value="payment">Payment</option>
              <option value="topup">Topup</option>
              <option value="mail">Mail</option>
              <option value="settings">Settings</option>
              <option value="advertisement">Advertisement</option>
              <option value="statistic">Statistic</option>
            </select>
          </div>
        </label>

        {/* Mark as seen toggle */}
        <div className="admin-modal-inline-row">
          <input type="checkbox" name="seen" checked={formData.seen} onChange={handleChange} disabled={saving} />
          <span>Mark as seen</span>
        </div>

        {/* Error message */}
        {error && (
          <div className="admin-modal-error-wrapper">
            <ErrorMessage message={error} onClose={() => setError("")} />
          </div>
        )}

        {/* Success message */}
        {success && (
          <div className="admin-modal-success-wrapper">
            <SuccessMessage message={success} onClose={() => setSuccess("")} />
          </div>
        )}
      </form>
    </Modal>
  );
};

export default AdminEditNotificationModal;
